// SemanticAnalyzerService.js
// Service pour l'intégration avec les API d'analyse sémantique du backend

import axios from 'axios';
import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

/**
 * Service pour l'analyse sémantique et la détection de catégories
 * Fournit une interface avec le backend pour l'analyse des objectifs
 */
export class SemanticAnalyzerService {
  /**
   * Obtient un token d'authentification Firebase
   * @returns {Promise<string>} - Token d'authentification
   */
  static async getAuthToken() {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }
    
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Erreur lors de la récupération du token:', error);
      throw error;
    }
  }

  /**
   * Analyse l'objectif de l'utilisateur à l'aide du backend
   * @param {string} objective - L'objectif principal de l'utilisateur
   * @param {string|null} query - Une requête spécifique supplémentaire (optionnel)
   * @param {Object} options - Options d'analyse supplémentaires
   * @returns {Promise<Object>} - Résultats de l'analyse sémantique
   */
  static async analyzeObjective(objective, query = null, options = {}) {
    if (!objective) {
      throw new Error('Un objectif est requis pour l\'analyse');
    }
    
    try {
      const token = await this.getAuthToken();
      
      const response = await axios.post(
        `${API_BASE_URL}/search/analyze-objective`,
        {
          userObjective: objective,
          query,
          options
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 && response.data.success) {
        return response.data.analysis;
      } else {
        console.error('Erreur lors de l\'analyse:', response.data.error);
        throw new Error(response.data.error || 'Échec de l\'analyse sémantique');
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API d\'analyse:', error);
      
      // Retourner un résultat de fallback en cas d'échec
      return this.generateFallbackAnalysis(objective, query);
    }
  }

  /**
   * Détecte les catégories les plus pertinentes pour un objectif
   * @param {string} objective - L'objectif de l'utilisateur
   * @param {number} limit - Nombre maximum de catégories à retourner
   * @returns {Promise<Array<string>>} - Catégories détectées
   */
  static async detectCategories(objective, limit = 3) {
    if (!objective) {
      return ['education', 'technology', 'news']; // Catégories par défaut
    }
    
    try {
      const token = await this.getAuthToken();
      
      const response = await axios.post(
        `${API_BASE_URL}/search/detect-categories`,
        {
          userObjective: objective,
          limit
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 && response.data.success) {
        return response.data.categories;
      } else {
        console.error('Erreur lors de la détection des catégories:', response.data.error);
        return ['education', 'technology', 'news']; // Catégories par défaut en cas d'erreur
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API de détection de catégories:', error);
      return ['education', 'technology', 'news']; // Catégories par défaut en cas d'erreur
    }
  }

  /**
   * Génère une analyse de secours en cas d'échec de l'API
   * @param {string} objective - L'objectif de l'utilisateur
   * @param {string|null} query - Une requête supplémentaire
   * @returns {Object} - Analyse générée localement
   */
  static generateFallbackAnalysis(objective, query = null) {
    // Détection simple de l'intention
    let intention = 'learn';
    const text = (objective + ' ' + (query || '')).toLowerCase();
    
    if (text.includes('trouver') || text.includes('chercher') || text.includes('localiser')) {
      intention = 'find';
    } else if (text.includes('compar') || text.includes('différence') || text.includes('versus') || text.includes('vs')) {
      intention = 'compare';
    } else if (text.includes('résou') || text.includes('problème') || text.includes('réparer')) {
      intention = 'solve';
    } else if (text.includes('explor') || text.includes('découvr') || text.includes('idées')) {
      intention = 'explore';
    }
    
    // Extraire des mots-clés simples
    const keyTerms = text
      .replace(/[.,;:!?()[\]{}«»""'']/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 3 && !['pour', 'avec', 'dans', 'comment', 'quels', 'quelles'].includes(word))
      .slice(0, 5);
    
    // Déterminer des catégories
    const categories = [];
    
    if (text.includes('apprendre') || text.includes('formation') || text.includes('cours')) {
      categories.push('education');
    }
    if (text.includes('technologie') || text.includes('développement') || text.includes('informatique')) {
      categories.push('technology');
    }
    if (text.includes('entreprise') || text.includes('business') || text.includes('société')) {
      categories.push('business');
    }
    
    // Si aucune catégorie n'est détectée, utiliser des valeurs par défaut
    if (categories.length === 0) {
      categories.push('education', 'technology');
    }
    
    // Générer des requêtes suggérées basiques
    const suggestedQueries = this.generateBasicSuggestedQueries(keyTerms, intention);
    
    return {
      primaryGoal: objective,
      secondaryQuery: query,
      originalLanguage: 'fr',
      normalizedObjective: objective,
      intention: intention,
      topicCategories: categories,
      informationNeeds: ['tutorials', 'courses', 'resources', 'examples', 'best practices'],
      keyTerms: keyTerms,
      searchScope: intention === 'explore' ? 'exploratory' : (intention === 'find' ? 'specific' : 'broad'),
      contentTypes: ['tutorial', 'reference', 'academic'],
      expectedDepth: 'detailed',
      temporalAspect: 'current',
      suggestedQueries: suggestedQueries,
      locallyAnalyzed: true
    };
  }

  /**
   * Génère des requêtes suggérées basiques en fonction des termes clés et de l'intention
   * @param {Array<string>} keyTerms - Termes clés extraits de l'objectif
   * @param {string} intention - Intention détectée
   * @returns {Array<string>} - Liste de requêtes suggérées
   */
  static generateBasicSuggestedQueries(keyTerms, intention) {
    if (!keyTerms || keyTerms.length === 0) {
      return [];
    }
    
    const mainTerm = keyTerms[0] || '';
    const secondTerm = keyTerms[1] || '';
    
    // Générer des requêtes personnalisées en fonction de l'intention
    let suggestedQueries = [];
    
    switch (intention) {
      case 'learn':
        suggestedQueries = [
          `tutoriel ${mainTerm} ${secondTerm}`,
          `apprendre ${mainTerm} ${secondTerm}`,
          `cours ${mainTerm} en ligne`
        ];
        break;
      case 'find':
        suggestedQueries = [
          `trouver ${mainTerm} ${secondTerm}`,
          `meilleur ${mainTerm} ${secondTerm}`,
          `où ${mainTerm} ${secondTerm}`
        ];
        break;
      case 'compare':
        suggestedQueries = [
          `${mainTerm} vs ${secondTerm}`,
          `différence entre ${mainTerm} et ${secondTerm}`,
          `comparaison ${mainTerm} ${secondTerm}`
        ];
        break;
      case 'solve':
        suggestedQueries = [
          `résoudre problème ${mainTerm} ${secondTerm}`,
          `solution pour ${mainTerm} ${secondTerm}`,
          `fixer ${mainTerm} ${secondTerm}`
        ];
        break;
      case 'explore':
        suggestedQueries = [
          `découvrir ${mainTerm} ${secondTerm}`,
          `idées ${mainTerm} ${secondTerm}`,
          `tendances ${mainTerm} ${secondTerm}`
        ];
        break;
      default:
        suggestedQueries = [
          `${mainTerm} ${secondTerm} guide`,
          `${mainTerm} ${secondTerm} astuces`,
          `${mainTerm} ${secondTerm} meilleur`
        ];
    }
    
    return suggestedQueries;
  }

  /**
   * Met à jour l'analyse avec un nouveau paramètre
   * @param {Object} analysis - L'analyse existante
   * @param {string} paramName - Le nom du paramètre à modifier
   * @param {*} newValue - La nouvelle valeur
   * @returns {Object} - L'analyse mise à jour
   */
  static updateAnalysisParam(analysis, paramName, newValue) {
    if (!analysis) return null;
    
    // Créer une copie de l'analyse existante
    const updatedAnalysis = { ...analysis };
    
    // Mettre à jour le paramètre
    updatedAnalysis[paramName] = newValue;
    
    // Logique spéciale pour certains paramètres
    if (paramName === 'intention') {
      // Mettre à jour les besoins d'information en fonction de la nouvelle intention
      const needsMap = {
        'learn': ['tutorials', 'courses', 'examples', 'documentation', 'resources'],
        'find': ['locations', 'contact_info', 'availability', 'specifications', 'details'],
        'compare': ['differences', 'similarities', 'pros_and_cons', 'features', 'prices'],
        'decide': ['reviews', 'recommendations', 'ratings', 'best_options', 'expert_opinions'],
        'solve': ['solutions', 'steps', 'troubleshooting', 'fixes', 'workarounds'],
        'explore': ['ideas', 'options', 'alternatives', 'possibilities', 'examples'],
        'evaluate': ['analysis', 'criteria', 'quality', 'performance', 'opinions'],
        'purchase': ['price', 'product_details', 'reviews', 'availability', 'shipping'],
        'play': ['game_details', 'rules', 'platforms', 'compatibility', 'reviews'],
        'travel': ['destination', 'accommodation', 'transportation', 'attractions', 'costs'],
        'relax': ['methods', 'locations', 'services', 'benefits', 'testimonials']
      };
      
      if (needsMap[newValue]) {
        updatedAnalysis.informationNeeds = needsMap[newValue];
      }
      
      // Mettre à jour également les requêtes suggérées en fonction de la nouvelle intention
      if (updatedAnalysis.keyTerms && updatedAnalysis.keyTerms.length > 0) {
        updatedAnalysis.suggestedQueries = this.generateBasicSuggestedQueries(
          updatedAnalysis.keyTerms,
          newValue
        );
      }
    }
    
    return updatedAnalysis;
  }

  /**
   * Génère une requête optimisée depuis le backend basée sur l'analyse
   * @param {Object} analysis - Résultats de l'analyse
   * @returns {Promise<string>} - Requête optimisée
   */
  static async generateOptimizedQuery(analysis) {
    if (!analysis) {
      return '';
    }
    
    try {
      const token = await this.getAuthToken();
      
      const response = await axios.post(
        `${API_BASE_URL}/search/generate-optimized-query`,
        { analysis },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 && response.data.success) {
        return response.data.optimizedQuery;
      } else {
        console.error('Erreur lors de la génération de la requête optimisée:', response.data.error);
        return this.generateFallbackOptimizedQuery(analysis);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API de génération de requête:', error);
      return this.generateFallbackOptimizedQuery(analysis);
    }
  }
  
  /**
   * Génère une requête optimisée en fallback en cas d'échec de l'API
   * @param {Object} analysis - Résultats de l'analyse
   * @returns {string} - Requête optimisée générée localement
   */
  static generateFallbackOptimizedQuery(analysis) {
    if (!analysis || !analysis.keyTerms || analysis.keyTerms.length === 0) {
      return '';
    }
    
    // Utiliser les mots-clés principaux
    let optimizedQuery = analysis.keyTerms.slice(0, 3).join(' ');
    
    // Ajouter un modificateur basé sur l'intention
    const intentionModifiers = {
      'learn': 'tutoriel',
      'find': 'trouver',
      'compare': 'comparaison',
      'decide': 'meilleur choix',
      'solve': 'solution',
      'explore': 'idées',
      'evaluate': 'avis',
      'purchase': 'acheter',
      'play': 'jouer',
      'travel': 'voyage',
      'relax': 'détente'
    };
    
    if (intentionModifiers[analysis.intention]) {
      optimizedQuery += ` ${intentionModifiers[analysis.intention]}`;
    }
    
    return optimizedQuery;
  }

  /**
   * Analyse l'objectif de l'utilisateur à l'aide du backend avec des fonctionnalités IA avancées
   * @param {string} objective - L'objectif principal de l'utilisateur
   * @param {string|null} query - Une requête spécifique supplémentaire (optionnel)
   * @param {Object} options - Options d'analyse supplémentaires
   * @returns {Promise<Object>} - Résultats de l'analyse sémantique avancée
   */
  static async analyzeObjectiveWithAI(objective, query = null, options = {}) {
    if (!objective) {
      throw new Error('Un objectif est requis pour l\'analyse');
    }
    
    try {
      const token = await this.getAuthToken();
      
      // Appeler la route qui utilise l'analyse IA
      const response = await axios.post(
        `${API_BASE_URL}/search/analyze-objective-ai`,
        {
          userObjective: objective,
          query,
          options
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 && response.data.success) {
        return response.data.analysis;
      } else {
        console.error('Erreur lors de l\'analyse:', response.data.error);
        throw new Error(response.data.error || 'Échec de l\'analyse sémantique avancée');
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API d\'analyse avancée:', error);
      
      // Retourner un résultat de fallback en cas d'échec
      return this.generateAdvancedFallbackAnalysis(objective, query);
    }
  }

  /**
   * Génère une analyse de secours avancée en cas d'échec de l'API d'analyse IA
   * Inclut des données supplémentaires par rapport à l'analyse de base
   * @param {string} objective - L'objectif de l'utilisateur
   * @param {string|null} query - Une requête supplémentaire
   * @returns {Object} - Analyse enrichie générée localement
   */
  static generateAdvancedFallbackAnalysis(objective, query = null) {
    // Commencer par l'analyse de base
    const baseAnalysis = this.generateFallbackAnalysis(objective, query);
    
    // Ajouter des champs supplémentaires qui seraient normalement fournis par l'IA
    return {
      ...baseAnalysis,
      confidenceScore: 0.7,
      entityRecognition: baseAnalysis.keyTerms.map(term => ({
        entity: term,
        type: term.length > 4 ? 'KEYWORD' : 'TERM',
        relevance: Math.random() * 0.3 + 0.7 // Valeur aléatoire entre 0.7 et 1.0
      })),
      sentimentAnalysis: {
        sentiment: 'neutral',
        confidence: 0.6
      },
      aiEnhanced: false,
      analysisMethod: 'rule-based-fallback',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Enrichit une analyse existante avec des suggestions de requêtes
   * @param {Object} analysis - L'analyse existante
   * @returns {Object} - L'analyse enrichie avec des suggestions
   */
  static enrichAnalysisWithSuggestions(analysis) {
    if (!analysis) return null;
    
    // Si l'analyse a déjà des suggestions, les conserver
    if (analysis.suggestedQueries && analysis.suggestedQueries.length > 0) {
      return analysis;
    }
    
    // Générer des suggestions basées sur les termes clés et l'intention
    const suggestedQueries = this.generateBasicSuggestedQueries(
      analysis.keyTerms || [],
      analysis.intention || 'learn'
    );
    
    // Créer une copie avec les nouvelles suggestions
    return {
      ...analysis,
      suggestedQueries: suggestedQueries
    };
  }
}

export default SemanticAnalyzerService;